import { Helmet } from "react-helmet";
import { FC, useEffect, useState, useMemo, useCallback } from "react";
import { lazy, Suspense, memo } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Box,
  CSSObject,
  Button,
  Typography,
  Slide,
  TextField,
  Divider,
  useMediaQuery,
} from "@mui/material";
import "react-responsive-carousel/lib/styles/carousel.min.css"; 
const LazyCarousel = lazy(() => import("react-responsive-carousel").then(m => ({default: m.Carousel})));
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import CheckIcon from "@mui/icons-material/Check";
import Header from "../../components/Header";  
const Footer = lazy(() => import("../../components/Footer"));

import ListingCardSquare from "../../components/MobileListingsCardHome";
import OptimizedImage from "../../components/OptimizedImage";

const IconList = lazy(() => import("../../components/IconList"));

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import { GetHomePageProperty } from "../../constants/constants";
import { gql, useQuery } from "@apollo/client";
import { subscribeToMailchimp } from "../../services/endpoints";

import { locationLinks, policyLinks } from "../../constants/links";

import "./Sticky.css";
import "./HomePage.scss";

// Component interfaces for better TypeScript support
interface LocationBoxProps {
  imageUrl: string;
  locationUrl: string;
  loading?: "lazy" | "eager";
}

interface CarouselArrowProps {
  clickHandler: () => void;
  sx?: Record<string, any>;
}

interface Props {
  sx?: CSSObject;
  footerMenus: {
    about: {
      text: string;
      link: string;
    }[];
    contact: {
      text: string;
      link: string;
    }[];
    blog: {
      text: string;
      link: string;
    }[];
    sitemap: {
      text: string;
      link: string;
    }[];
  };
}

const HomePage: FC<Props> = () => {
  const navigate = useNavigate();

 
  const {
    data: newData,
    loading,
    error,
  } = useQuery(
    gql`
      ${GetHomePageProperty}
    `,
    { variables: {} }
  );

  const [showLocations, setShowLocations] = useState(false);
  const [showPetPolicies, setShowPetPolicies] = useState(false);
  const [showFull, setShowFull] = useState(false);
  const [showSticky, setShowSticky] = useState(false);
  const [enableCarousel, setEnableCarousel] = useState(false);

  /* Method that will fix header after a specific scrollable */
  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY;
    setShowSticky(scrollTop >= 200);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  const mobile = useMediaQuery("(max-width:800px)");

  // Memoize static data to prevent re-creation on every render
  const pricesOne = useMemo(() => [149, 129, 99], []);
  const pricesTwo = useMemo(() => [175, 189, 199], []);

  // Memoize expensive computations
  const sortedLocationLinks = useMemo(() => {
    return [...locationLinks].sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const RightPhotoBox = ({
    imgSrc,
    imgAlt,
    backgroundColor,
    fontColor = "black",
    header,
    text,
    cta,
    extraText,
  }) => (
    <Box
      sx={{
        width: "100%",
        backgroundColor: backgroundColor,
        display: { xs: "flex" },
        justifyContent: "center",
        alignItems: "flex-start",
        boxSizing: "border-box",
        flexDirection: {
          xs: "column-reverse",
          sm: "column-reverse",
          md: "row",
        },
        pl: {
          xs: 0,
          sm: 0,
          md: "1rem",
        },
      }}
    >
      <Box
        sx={{
          p: { xs: "1.5rem", sm: "1.5rem", md: 0 },
          maxWidth: { xs: "auto", sm: "auto", md: "440px" },
          mx: "auto",
          my: !extraText ? "auto" : undefined,
        }}
      >
        <Box
          sx={extraText ? { mt: { md: "2rem", lg: "5rem" } } : { mt: "auto" }}
        >
          <Typography
            variant="h2"
            sx={{
              color: fontColor,
              textAlign: { xs: "left", sm: "left", md: "left" },
              mt: { xs: "1rem", sm: "1rem", md: 0 },
              mb: extraText ? { xs: "1rem", lg: "1.5rem" } : "1.5rem",
              fontSize: extraText
                ? { xs: "1.25rem", md: "1.25rem", lg: "auto" }
                : "auto",
            }}
          >
            {header}
          </Typography>
          <Typography
            variant="p"
            sx={{
              color: fontColor,
              fontSize: extraText ? { xs: "1rem", lg: "1.25rem" } : "auto",
              mb: "2rem",
            }}
          >
            {text}
          </Typography>
          <Box
            sx={{
              textAlign: { xs: "center", sm: "center", md: "left" },
              mt: extraText ? { xs: "1.2rem", md: "1rem", lg: "2rem" } : "2rem",
              mb: extraText
                ? { xs: "1.2rem", md: "1rem", lg: "1.5rem" }
                : "1.5rem",
            }}
          >
            {cta}
          </Box>
        </Box>
        {extraText && (
          <Typography
            variant="body2"
            sx={{
              color: fontColor,
              mt: { xs: "2rem", md: "2.5rem", lg: "5rem" },
              mb: "1rem",
              fontWeight: { xs: 0.1, lg: 1 },
              fontSize: { xs: "0.75rem", md: "0.6rem", lg: "0.8rem" },
            }}
          >
            {extraText}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          marginLeft: "auto",
          maxWidth: {
            xs: "100%",
            sm: "100%",
            md: "500px",
            lg: "800px",
            xl: "800px",
          },
        }}
      >
        <OptimizedImage
          src={imgSrc}
          alt={imgAlt}
          width={800}
          height={600}
          style={{
            width: "100%",
            height: "auto",
          }}
        />
      </Box>
    </Box>
  );

  const SignUpEmail = () => {
    const [isSuccess, setIsSuccess] = useState(false);
    const [email, setEmail] = useState("");
    return (
      <form
        style={{ marginTop: "1.25rem" }}
        onSubmit={(e) => {
          e.preventDefault();
          // subscribeToNewsletter(email);
          subscribeToMailchimp(email.trim(), "New Subscriber");
        }}
      >
        <TextField
          sx={{
            "& .MuiInput-underline:before": { borderBottomColor: "white" },
            "& .MuiInput-underline:after": { borderBottomColor: "white" },
            input: { color: "black" },
            width: { xs: "100%", sm: "100%", md: "300px" },
          }}
          variant="standard"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target?.value)}
          required
          placeholder="Email address"
          InputProps={{
            endAdornment: isSuccess ? (
              <Box
                sx={{
                  backgroundColor: "white",
                  width: "24px",
                  height: "24px",
                  borderRadius: "100%",
                  cursor: "pointer",
                }}
              >
                <CheckIcon />
              </Box>
            ) : (
              <Box
                onClick={() => {
                  setIsSuccess(false);
                  const result = subscribeToMailchimp(
                    email.trim(),
                    "New Subscriber"
                  );
                  setIsSuccess(result.success);
                }}
                sx={{
                  backgroundColor: "white",
                  width: "24px",
                  height: "24px",
                  borderRadius: "100%",
                  cursor: "pointer",
                }}
              >
                <PlayArrowOutlinedIcon />
              </Box>
            ),
          }}
        />
      </form>
    );
  };

  // Memoized LocationBox component for better performance
  const LocationBox = memo<LocationBoxProps>(({ imageUrl, locationUrl, loading = "lazy" }) => (
    <a href={locationUrl}>
      <Box>
        <OptimizedImage
          src={imageUrl}
          alt={locationUrl}
         

          loading={loading}
          style={{
            objectFit: "cover",
            borderRadius: "8px 8px 8px 8px",
            maxWidth: "306px",
            
          }}
        />
          <Typography
          variant="p"
          sx={{
            textDecoration: "none",
            textAlign: "center",
            position: "absolute",
            bottom: "0",
            left: "0",
            right: "0",
            backgroundColor: "#03989E",
            marginBottom: "10%",
            marginLeft: "auto",
            marginRight: "auto",
            width: "70%",
            padding: "15px",
            borderRadius: "50px",
            color: "white",
            fontWeight: "bold",
            maxWidth: "250px",
          }}
        >
          Explore
        </Typography>
      </Box>
    </a>
  ));
  LocationBox.displayName = 'LocationBox';

  // Memoized Arrow Components for Carousel
  const CarouselArrowPrev = memo<CarouselArrowProps>(({ clickHandler, sx = {} }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        top: "45%",
        left: 0,
        boxShadow: 2,
        borderRadius: "100%",
        position: "absolute",
        backgroundColor: "white",
        p: "1rem",
        zIndex: 50,
        cursor: "pointer",
        ...sx,
      }}
      onClick={clickHandler}
    >
      <ArrowBackIosNewIcon sx={{ width: "20px", height: "20px" }} />
    </Box>
  ));
  CarouselArrowPrev.displayName = 'CarouselArrowPrev';

  const CarouselArrowNext = memo<CarouselArrowProps>(({ clickHandler, sx = {} }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        top: "45%",
        right: 0,
        boxShadow: 2,
        borderRadius: "100%",
        position: "absolute",
        backgroundColor: "white",
        p: "1rem",
        zIndex: 50,
        cursor: "pointer",
        ...sx,
      }}
      onClick={clickHandler}
    >
      <ArrowForwardIosIcon sx={{ width: "20px", height: "20px" }} />
    </Box>
  ));
  CarouselArrowNext.displayName = 'CarouselArrowNext';

  const carouselRef = useCallback((node: HTMLElement | null) => {
    if (!node) return;
    if ('IntersectionObserver' in window) {
      const obs = new IntersectionObserver(entries => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            setEnableCarousel(true);
            obs.disconnect();
          }
        });
      }, { rootMargin: '200px 0px' });
      obs.observe(node);
    } else { 
      setEnableCarousel(true);
    }
  }, []);

  return (
    <div className="homepage">
      <Helmet>
        <title>Pet Friendly Hotels & Vacation Rentals | Romingo</title>
        {/*<description>Find and book dog-friendly hotels easily with Romingo. Explore a wide range of pet-friendly accommodations, transparent pet policies, and luxury amenities for your furry friends. Plan your next vacation with confidence and ensure a perfect stay for both you and your dog!</description>*/}
        <meta
          property="og:title"
          content="Pet Friendly Hotels & Vacation Rentals | Romingo"
        />
        <meta property="og:url" content="https://www.romingo.com" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Romingo" />
        <meta
          name="twitter:title"
          content="Book pet friendly hotels — Romingo"
        />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {showSticky && (
        <>
          <Box
            sx={{ display: { xs: "none", sm: "none", md: "block" } }}
            position="fixed"
            top="31px"
            right={{ lg: "465px", md: "250px" }}
            zIndex="9999"
          >
            <Box
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              color="blue"
              sx={{
                color: "#03989E",
                cursor: "pointer",
                "&:hover": {
                  color: "#0000A3",
                },
              }}
            >
              Book Now
            </Box>
          </Box>
          {/*
        <Box sx={{ display: { xs: 'block', sm: 'block', md: 'none' } }}
          position="fixed"
          bottom="20px"
          left="25px"
          zIndex="9999"
        >
          <Button
            variant="contained"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >Book Now</Button>
        </Box>
        */}
        </>
      )}

      <Header />

      <Box sx={{ mx: "auto", py: "1rem", height: { md: "auto", lg: "auto" } }}>
        <Suspense fallback={<div style={{ minHeight: "250px" }}> </div>}>
          <IconList isCarousel={false} />
        </Suspense>
      </Box>

      <Box
        sx={{
          display: { xs: "block", sm: "block", md: "none" },
          mx: { xs: "0rem", sm: "auto", md: "auto" },
          px: { xs: "1rem", sm: "1rem" },
          py: { xs: "1rem", sm: "1rem" },
          borderRadius: "10px",
          maxWidth: { xs: "100%", sm: "700px" },
          height: { md: "auto", lg: "auto" },
        }}
      >
        <Box
          sx={{
            mx: { xs: "0", md: "auto" },
            mt: "1.5rem",
            p: { xs: "1rem", sm: "1rem" },
            borderRadius: "12px",
            maxWidth: "700px",
            background: "linear-gradient(135deg, #03989E, #026D74)",
            color: "white",
            textAlign: "center",
            boxShadow: "0px 5px 12px rgba(0, 0, 0, 0.2)",
          }}
        >
          <Typography variant="h5" fontWeight={600} mb={1.8}>
            🎉 Join Romingo Rewards today!
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mb: 2,
              fontSize: "1.1rem",
              fontWeight: 300,
              px: { xs: "1rem", sm: "4rem" },
            }}
          >
            Sign up to earn <b>pawsome perks</b> and <b>free travel rewards</b>{" "}
            when you book pet-friendly stays with Romingo.
          </Typography>

          <Button
            variant="contained"
            sx={{
              mt: 2,
              backgroundColor: "white",
              color: "#026D74",
              borderRadius: "10px",
              px: "24px",
              py: "8px",
              fontSize: "1rem",
              fontWeight: "600",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.9)",
              },
            }}
            onClick={() => navigate("/create-account")}
          >
            🐾 Sign Up Now
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          mt: { xs: "3rem", sm: "3rem", md: "0rem" },
          marginBottom: { sm: 0, md: 0, lg: 0 },
          pb: 0,
          background: "white",
        }}
      >
        <Box
          sx={{
            mx: { xs: "0.5em", sm: "1em", lg: "0.6em" },
            mb: "2rem",
          }}
        >
          <Box
            sx={{
              mx: "auto",
              maxWidth: "1200px",
              py: { xs: "0rem", sm: "0rem", md: "5rem" },
            }}
          >
            <Typography
              variant="h4"
              sx={{ mb: "1rem", textAlign: { xs: "center", md: "left" } }}
            >
              Where to next?
            </Typography>
            {mobile ? (
              enableCarousel ? (
                <Suspense fallback={<div style={{height:220}} />}> 
                  <LazyCarousel
                    infiniteLoop={true}
                    showStatus={false}
                    showIndicators={false}
                    showArrows={mobile ? false : true}
                    showThumbs={false}
                    centerSlidePercentage={50}
                    centerMode={mobile ? false : true}
                    swipeScrollTolerance={60}
                    renderArrowPrev={(clickHandler, hasPrev) => (
                      <CarouselArrowPrev clickHandler={clickHandler} />
                    )}
                    renderArrowNext={(clickHandler, hasPrev) => (
                      <CarouselArrowNext clickHandler={clickHandler} />
                    )}
                  >
                    <Box key="sd" sx={{ px: "0.75rem", mx: "1rem" }}>
                      <LocationBox
                        imageUrl="https://storage.googleapis.com/romingo-production-public/other-pages/d-1.4b58ebe74aa3c7dd6d81.webp"
                        locationUrl="pet-friendly-hotels/san-diego-california"
                        loading="eager"
                      />
                    </Box>
                    <Box key="chi" sx={{ px: "0.75rem", mx: "auto" }}>
                      <LocationBox
                        imageUrl="https://storage.googleapis.com/romingo-production-public/other-pages/chicago.webp"
                        locationUrl="pet-friendly-hotels/chicago-illinois"
                        loading="eager"
                      />
                    </Box>
                    <Box key="sf" sx={{ px: "0.75rem", mx: "auto" }}>
                      <LocationBox
                        imageUrl="https://storage.googleapis.com/romingo-production-public/other-pages/d-4.2aee34d9490cd3593cee.webp"
                        locationUrl="pet-friendly-hotels/san-francisco-california"
                        loading="lazy"
                      />
                    </Box>
                    <Box key="lv" sx={{ px: "0.75rem", mx: "auto" }}>
                      <LocationBox
                        imageUrl="https://storage.googleapis.com/romingo-production-public/other-pages/d-5.b9fadcaa82613bc8f3c2.webp"
                        locationUrl="pet-friendly-hotels/las-vegas-nevada"
                        loading="lazy"
                      />
                    </Box>
                    <Box key="ny" sx={{ px: "0.75rem", mx: "auto" }}>
                      <LocationBox
                        imageUrl="https://storage.googleapis.com/romingo-production-public/other-pages/d-6.1b1e1e1e58ffedbda62e.webp"
                        locationUrl="pet-friendly-hotels/new-york-new-york"
                        loading="lazy"
                      />
                    </Box>
                    <Box key="sea" sx={{ px: "0.75rem", mx: "auto" }}>
                      <LocationBox
                        imageUrl="https://storage.googleapis.com/romingo-production-public/other-pages/d-9.aae29b036d225f173fce%20(1).webp"
                        locationUrl="pet-friendly-hotels/seattle-washington"
                        loading="lazy"
                      />
                    </Box>
                  </LazyCarousel>
                </Suspense>
              ) : (
                <div ref={carouselRef} style={{height:220}} />
              )
            ) : (
              enableCarousel ? (
                <Suspense fallback={<div style={{height:260}} />}> 
                  <LazyCarousel
                    infiniteLoop={true}
                    showStatus={false}
                    showIndicators={false}
                    showArrows={mobile ? false : true}
                    showThumbs={false}
                    centerSlidePercentage={mobile ? 100 : 33} // Show one full slide with half of each on the sides
                    centerMode={true}
                    swipeScrollTolerance={60}
                    renderArrowPrev={(clickHandler, hasPrev) => (
                      <CarouselArrowPrev clickHandler={clickHandler} />
                    )}
                    renderArrowNext={(clickHandler, hasPrev) => (
                      <CarouselArrowNext clickHandler={clickHandler} />
                    )}
                  >
                    <Box key="sd-d" sx={{ px: "0.75rem", mx: "auto" }}>
                      <LocationBox
                        imageUrl="https://storage.googleapis.com/romingo-production-public/other-pages/d-1.4b58ebe74aa3c7dd6d81.webp"
                        locationUrl="pet-friendly-hotels/san-diego-california"
                      />
                    </Box>
                    <Box key="chi-d" sx={{ px: "0.75rem", mx: "auto" }}>
                      <LocationBox
                        imageUrl="https://storage.googleapis.com/romingo-production-public/other-pages/chicago.webp"
                        locationUrl="pet-friendly-hotels/chicago-illinois"
                      />
                    </Box>
                    <Box key="sf-d" sx={{ px: "0.75rem", mx: "auto" }}>
                      <LocationBox
                        imageUrl="https://storage.googleapis.com/romingo-production-public/other-pages/d-4.2aee34d9490cd3593cee.webp"
                        locationUrl="pet-friendly-hotels/san-francisco-california"
                      />
                    </Box>
                    <Box key="lv-d" sx={{ px: "0.75rem", mx: "auto" }}>
                      <LocationBox
                        imageUrl="https://storage.googleapis.com/romingo-production-public/other-pages/d-5.b9fadcaa82613bc8f3c2.webp"
                        locationUrl="pet-friendly-hotels/las-vegas-nevada"
                      />
                    </Box>
                    <Box key="ny-d" sx={{ px: "0.75rem", mx: "auto" }}>
                      <LocationBox
                        imageUrl="https://storage.googleapis.com/romingo-production-public/other-pages/d-6.1b1e1e1e58ffedbda62e.webp"
                        locationUrl="pet-friendly-hotels/new-york-new-york"
                      />
                    </Box>
                    <Box key="sea-d" sx={{ px: "0.75rem", mx: "auto" }}>
                      <LocationBox
                        imageUrl="https://storage.googleapis.com/romingo-production-public/other-pages/d-9.aae29b036d225f173fce%20(1).webp"
                        locationUrl="pet-friendly-hotels/seattle-washington"
                      />
                    </Box>
                  </LazyCarousel>
                </Suspense>
              ) : (
                <div ref={carouselRef} style={{height:260}} />
              )
            )}
          </Box>
        </Box>
        <Box
          sx={{
            boxSizing: "border-box",
          }}
        >
          <RightPhotoBox
            imgSrc="https://storage.googleapis.com/romingo-production-public/other-pages/home-hero.88e56fff0aef402dc2ab.webp"
            imgAlt="romingo rewards"
            backgroundColor="#F2EBE4"
            fontColor="black"
            header={
              <Typography
                variant="h4"
                sx={{ mb: "1rem", textAlign: { xs: "center", md: "left" } }}
              >
                Romingo rewards 🐶
              </Typography>
            }
            text="Get treated when traveling with your pet! Earn real money towards future travel when you book your pet friendly trip with Romingo."
            cta={
              <Button
                sx={{ width: "300px" }}
                variant="contained"
                onClick={() => navigate("/romingo-rewards")}
              >
                Learn more
              </Button>
            }
            extraText="*Joining Romingo Rewards is 100% free for all guests. When booking your stay, simply log into your account, complete your purchase, and earn free rewards. View the full terms & conditions at Romingo Rewards."
          />
        </Box>

        {/*   <Box sx={{ mt: { xs: '0rem', sm: '0rem', md: '0rem' }, mb: 0, pb: 0 }}>
          <NewLeftPhotoBox
            imgSrc={[reviewOne, reviewTwo, reviewThree, reviewFour]}
            loading="lazy"
            imgAlt="Left Photo"
            backgroundColor="#f1ebe5"
            fontColor="black"
            header={
              <Typography variant="h4" sx={{ mb: '1rem', textAlign: { xs: 'center', md: 'center' } }}>
                Over 25,000 travelers and their pets have experienced pet friendly travel with Romingo
              </Typography>
            }

            text="Roam the world freely with Romingo. With endless tail-wagging adventures, new discoveries, and plenty of treats along the way, Romingo invites you to experience pet friendly travel reimagined."
            cta={<TestimonialsSlider />}
          />
        </Box>*/}

        <Box
          sx={{
            gap: "1.5rem",
            maxWidth: "1200px",
            px: "15px",
            mx: "auto",
            mt: { xs: "2rem", sm: "2rem", md: "4rem" },
            mb: "2rem",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              marginLeft: "0 !important",
              mb: "1rem",
              textAlign: { xs: "center", md: "left" }, // Center on mobile, left-align on larger screens
              ml: { xs: 0, sm: 0, md: "0.6em" }, // No margin on mobile, specific margin on larger screens
            }}
          >
            Pet-approved favorites
          </Typography>

          {enableCarousel ? (
            <Suspense fallback={<div style={{height:260}} />}> 
              <LazyCarousel
                infiniteLoop={true}
                showStatus={false}
                showIndicators={false}
                showArrows={mobile ? false : true}
                showThumbs={false}
                centerSlidePercentage={mobile ? 100 : 33}
                centerMode={true}
                swipeScrollTolerance={60}
                renderArrowPrev={(clickHandler, hasPrev) => (
                  <CarouselArrowPrev clickHandler={clickHandler} />
                )}
                renderArrowNext={(clickHandler, hasPrev) => (
                  <CarouselArrowNext clickHandler={clickHandler} />
                )}
              >
                {loading ? (
                  <div></div>
                ) : (
                  newData?.getHomepagePropertiesThree.map((hotel, i) => (
                    <Box
                      key={i}
                      sx={{
                        p: "1em",
                        maxWidth: { xs: "100%", sm: "90%", md: "400px" },
                        mx: "auto",
                      }}
                    >
                      <ListingCardSquare
                        {...hotel}
                        name={
                          hotel.hotelName === "Margaritaville Resort Palm Springs"
                            ? "Margitaville Resort"
                            : hotel.hotelName
                        }
                        lowestTotalPriceAfterTax={
                          i < 3 ? pricesOne[i] : pricesTwo[i - 3]
                        }
                        highlighted={false}
                      />
                    </Box>
                  ))
                )}
              </LazyCarousel>
            </Suspense>
          ) : (
            <div ref={carouselRef} style={{height:260}} />
          )}
        </Box>
      </Box>

      <Box
        onClick={() => setShowLocations(!showLocations)}
        sx={{
          display: { sm: "block", md: "none" },
          boxShadow: 2,
          "&:hover": { boxShadow: 3 },
          textAlign: "center",
          p: "2em",
          borderRadius: "20px",
          mx: "1rem",
          mt: "2rem",
          mb: "0.25rem",
        }}
      >
        <Box
          sx={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ fontSize: "1.2em" }}>
            Explore destinations
          </Typography>
          <KeyboardArrowDownIcon color="primary" />
        </Box>
      </Box>

      <Box
        onClick={() => setShowPetPolicies(!showPetPolicies)}
        sx={{
          display: { sm: "block", md: "none" },
          boxShadow: 2,
          "&:hover": { boxShadow: 3 },
          textAlign: "center",
          p: "2em",
          borderRadius: "20px",
          mx: "1rem",
          mt: "2rem",
          mb: "1.25rem",
        }}
      >
        <Box
          sx={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ fontSize: "1.2em" }}>Hotel pet policies</Typography>
          <KeyboardArrowDownIcon color="primary" />
        </Box>
      </Box>

      <Box
        sx={{ 
          display: { xs: "none", sm: "none", md: "block" },
          backgroundColor: "#ffffff",
          p: "2rem",
          mt: "2rem"
        }}
      >
        <Box
          sx={{
            mx: "auto",
            maxWidth: "760px",
            mt: "4rem",
            mb: "8rem",
            p: "1.5rem",
            boxShadow: 2,
            gap: "1rem",
            borderRadius: "20px",
            backgroundColor: "white",
            height: showFull ? "auto" : "220px",
            overflow: "hidden",
          }}
        >
          <Box>
            <Typography variant="h4" mb="1rem" textAlign="center">
              Plan your next trip with Romingo
            </Typography>
          </Box>
          {!showFull && (
            <Box position="relative">
              <Box
                onClick={() => setShowFull(!showFull)}
                sx={{
                  textAlign: "center",
                  position: "absolute",
                  top: "160px",
                  backgroundColor: "white",
                  width: "100%",
                  py: "0.25rem",
                  "&:hover": { backgroundColor: "#D3D3D3" },
                }}
              >
                <KeyboardArrowDownIcon sx={{ color: "white" }} />
              </Box>
            </Box>
          )}
          <Box
            display="flex"
            flexDirection="row"
            sx={{ justifyContent: "space-around" }}
          >
            <Box display="flex" flexDirection="column" gap="0.5rem">
              <Typography
                borderBottom="1px solid black"
                variant="p"
                fontSize="1.25rem"
              >
                Explore our destinations
              </Typography>
              {sortedLocationLinks.map((link) => (
                <Box key={link.to}>
                  <a key={link.to} href={link.to}>
                    <Typography variant="base">
                      {link.name} pet-friendly hotels
                    </Typography>
                  </a>
                </Box>
              ))}
            </Box>
            <Box display="flex" flexDirection="column" gap="0.5rem">
              <Typography
                borderBottom="1px solid black"
                variant="p"
                fontSize="1.25rem"
              >
                Hotel pet policies
              </Typography>
              {policyLinks.map((link) => (
                <Box key={link.to}>
                  <a key={link.to} href={link.to}>
                    <Typography variant="base">
                      {link.name} pet-friendly hotels
                    </Typography>
                  </a>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      <div id="cta">
        <RightPhotoBox
          imgSrc="https://storage.googleapis.com/romingo-production-public/other-pages/homepage-dog.634f371aae69c579d7da.webp"
          imgAlt="sign up for romingo exclusive deals"
          backgroundColor="#F2EBE4"
          fontColor="black"
          header={
            <Typography
              variant="h4"
              sx={{ mb: "1rem", textAlign: { xs: "center", md: "left" } }}
            >
              Sign up for Romingo exclusive deals and pet-friendly tips
            </Typography>
          }
          text="Get access to discounted rates and last-minute travel deals, plus $20 off your first stay."
          cta={<SignUpEmail />}
          extraText={undefined}
        />
      </div>

      <Slide direction="up" in={showLocations} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: "fixed",
            overflow: "auto",
            bottom: "0",
            height: "80%",
            width: "100%",
            backgroundColor: "white",
            borderTopRightRadius: "20px",
            borderTopLeftRadius: "20px",
            zIndex: 1000,
          }}
        >
          <Box
            position="relative"
            width="90%"
            textAlign="center"
            sx={{ m: "1rem", mt: "1.5rem", backgroundColor: "white" }}
          >
            <Typography textAlign="center" variant="h5">
              Explore destinations
            </Typography>
            <Button
              sx={{ position: "absolute", top: -6, right: 0 }}
              variant="outlined"
              onClick={() => setShowLocations(false)}
            >
              X
            </Button>
          </Box>
          <Box height="88%" overflow="scroll">
            {sortedLocationLinks.map((link) => (
              <a
                key={link.to}
                href={link.to}
                style={{ textDecoration: "none", color: "black" }}
              >
                <Box
                  sx={{
                    px: "1.25rem",
                    py: "0.75rem",
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "#d9f7fc" },
                  }}
                >
                  <Typography variant="p">{link.name}</Typography>
                </Box>
              </a>
            ))}
          </Box>
        </Box>
      </Slide>

      <Slide direction="up" in={showPetPolicies} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: "fixed",
            overflow: "auto",
            bottom: "0",
            height: "80%",
            width: "100%",
            backgroundColor: "white",
            borderTopRightRadius: "20px",
            borderTopLeftRadius: "20px",
            zIndex: 1000,
          }}
        >
          <Box
            position="relative"
            width="90%"
            textAlign="center"
            sx={{ m: "1rem", mt: "1.5rem", backgroundColor: "white" }}
          >
            <Typography textAlign="center" variant="h5">
              Hotel pet policies
            </Typography>
            <Button
              sx={{ position: "absolute", top: -6, right: 0 }}
              variant="outlined"
              onClick={() => setShowPetPolicies(false)}
            >
              X
            </Button>
          </Box>
          <Box height="88%" overflow="scroll">
            {policyLinks.map((link) => (
              <a
                key={link.to}
                href={link.to}
                style={{ textDecoration: "none", color: "black" }}
              >
                <Box
                  sx={{
                    px: "1.25rem",
                    py: "0.75rem",
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "#d9f7fc" },
                  }}
                >
                  <Typography variant="p">{link.name}</Typography>
                </Box>
              </a>
            ))}
          </Box>
        </Box>
      </Slide>

      <Suspense fallback={<div>Loading footer...</div>}>
        <Footer />
      </Suspense>
    </div>
  );
};

export default HomePage;
